import React from "react";
import MetaTags from "@components/shared/metaTags";
import {
    GQLAttachments,
    GQLPosts,
    GQLTags,
    GQLUrls,
    GQLUsers,
} from "@gql/types/gql";
import { GetStaticPropsContext } from "next";
import initialCall, { initialCallPaths } from "@helpers/initial-call";
import { GQLDomains } from "@gql/types/gql";
import { useQuery } from "@tanstack/react-query";
import { translate } from "@helpers/translationObjectFormatter";
import { ICustomDomain, TranslationsObject } from "@genericTypes/sharedTypes";
import {
    getDescriptionForMetaTags,
    getKeyWordsForMetaTags,
} from "@lib/sharedUtils";
import styles from "../styles.module.scss";
import Pagination from "@components/shared/Pagination/Pagination";
import NewsCard from "@components/shared/NewsCard/NewsCard";
import { titleDecoder } from "@lib/sharedUtils";
const Page = () => {
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: writerInfo } = useQuery<GQLUsers>(["writerPostsInfo"]);
    const { data: posts } = useQuery<GQLPosts[]>(["writerPostsPagination"]);
    const { data: total } = useQuery<number>(["writerPostsTotal"]);
    const { data: currentPage } = useQuery<number>(["writerPostsCurrentPage"]);

    return (
        <>
            <MetaTags
                title={`${writerInfo?.nickname as string} | Several.com`}
                description={
                    getDescriptionForMetaTags(
                        domainInfo?.domain as GQLDomains,
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                url={`/writers/${writerInfo?.nicknameSlug as string}${
                    currentPage && currentPage !== 1 ? `/${currentPage}` : ""
                }`}
                keyWords={
                    getKeyWordsForMetaTags(
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
                image="public/images25.png"
            />
            <section className="relative max-w-7xl mx-auto px-5">
                <div className={styles["section-background"]}>
                    <div className="lg:pt-20 pt-10 lg:pb-16 pb-8">
                        <h1 className="text-4xl font-bold ">
                            {writerInfo?.nickname}
                        </h1>
                        {writerInfo?.position && (
                            <span className="block text-lg">
                                {writerInfo?.position}
                            </span>
                        )}
                        {writerInfo?.bio && (
                            <div
                                className="mt-5 font-light"
                                dangerouslySetInnerHTML={{
                                    __html: titleDecoder(writerInfo?.bio),
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="mt-12 lg:text-3xl text-xl font-bold">
                    {translate(translations, "several_writersPage_posted_by")}{" "}
                    {writerInfo?.nickname}
                </div>
                <div className="mb-[3rem] lg:mt-8 mt-4">
                    <div className="flex flex-wrap items-center gap-8">
                        {posts?.map((item) => (
                            <NewsCard
                                key={item.title}
                                image={item.image as GQLAttachments}
                                title={item.title as string}
                                tags={item.tags as GQLTags[]}
                                url={item.url as GQLUrls}
                                createdAt={item.createdAt as string}
                                hideTag
                            />
                        ))}
                    </div>
                </div>
                <div className="w-[100%] sm:w-[80%] md:w-[50%] lg:w-[30%] mx-auto mb-[5rem]">
                    <Pagination
                        key={currentPage}
                        total={total as number}
                        offset={12}
                        currentPage={currentPage ?? 0}
                        url={`/writers/${writerInfo?.nicknameSlug as string}`}
                    />
                </div>
            </section>
        </>
    );
};

export function getStaticPaths() {
    return initialCallPaths();
}

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
    return await initialCall("WritersPage", ctx);
};
export default Page;
